/**
 * ProjectorView - Live Dashboard for Presentation Halls
 * Designed for large screens showing real-time polls and Q&A
 * No buttons or menus - purely display-focused
 */
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  MessageCircle, ThumbsUp, Users, BarChart3, 
  Mic, Clock, Zap, TrendingUp 
} from 'lucide-react';
import api from '@/lib/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ProjectorView = ({ stageId, eventId }) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update clock every second
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Fetch stage/session data
  const { data: stage } = useQuery({
    queryKey: ['stage', stageId],
    queryFn: () => api.get(`/stages/${stageId}`).then(r => r.data),
    refetchInterval: 5000,
  });

  // Fetch active poll
  const { data: activePoll } = useQuery({
    queryKey: ['active-poll', stageId],
    queryFn: () => api.get(`/stages/${stageId}/polls/active`).then(r => r.data).catch(() => null),
    refetchInterval: 2000, // Fast refresh for live updates
  });

  // Fetch top questions
  const { data: questions } = useQuery({
    queryKey: ['questions', stageId],
    queryFn: () => api.get(`/stages/${stageId}/questions`).then(r => r.data),
    refetchInterval: 3000,
  });

  // Fetch audience count
  const { data: audienceStats } = useQuery({
    queryKey: ['audience', stageId],
    queryFn: () => api.get(`/stages/${stageId}/audience`).then(r => r.data).catch(() => ({ count: 0 })),
    refetchInterval: 10000,
  });

  const topQuestions = questions?.filter(q => !q.answered)?.sort((a, b) => b.votes - a.votes)?.slice(0, 5) || [];
  const totalVotes = activePoll?.options?.reduce((sum, opt) => sum + (opt.votes || 0), 0) || 0;

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-hidden" data-testid="projector-view">
      {/* Ambient Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute w-full max-w-[600px] h-[600px] rounded-full blur-[120px] opacity-20"
          style={{ 
            background: 'radial-gradient(circle, #3B82F6, transparent)',
            top: '-10%',
            left: '-10%',
            animation: 'pulse 8s ease-in-out infinite'
          }}
        />
        <div 
          className="absolute w-full max-w-[500px] h-[500px] rounded-full blur-[100px] opacity-15"
          style={{ 
            background: 'radial-gradient(circle, #10B981, transparent)',
            bottom: '-5%',
            right: '-5%',
            animation: 'pulse 10s ease-in-out infinite reverse'
          }}
        />
      </div>

      {/* Header Bar */}
      <div className="relative z-10 flex items-center justify-between px-12 py-6 bg-black/30 backdrop-blur-sm">
        <div className="flex items-center gap-6">
          <Badge variant="secondary" className="text-lg px-4 py-2 bg-emerald-500/20 text-emerald-400 border-emerald-500/50">
            <Zap className="h-4 w-4 mr-2" />
            LIVE
          </Badge>
          <h1 className="text-2xl font-bold">{stage?.title || 'Main Stage'}</h1>
        </div>
        <div className="flex items-center gap-8 text-white/60">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            <span className="text-xl font-semibold text-white">{audienceStats?.count || 0}</span>
            <span>watching</span>
          </div>
          <div className="text-2xl font-mono">
            {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex h-[calc(100vh-100px)]">
        {/* Left Panel - Live Poll */}
        <div className="flex-1 p-12 flex flex-col">
          {activePoll ? (
            <div className="flex-1 flex flex-col animate-in fade-in duration-500">
              <div className="flex items-center gap-4 mb-8">
                <BarChart3 className="h-10 w-10 text-blue-400" />
                <h2 className="text-4xl font-bold">Live Poll</h2>
                <Badge variant="outline" className="ml-auto text-lg px-4 py-2 border-blue-400 text-blue-400 animate-pulse">
                  Voting Open
                </Badge>
              </div>
              
              <h3 className="text-3xl mb-12 text-white/90">{activePoll.question}</h3>
              
              <div className="space-y-6 flex-1">
                {activePoll.options?.map((option, index) => {
                  const percentage = totalVotes > 0 ? Math.round((option.votes / totalVotes) * 100) : 0;
                  const isLeading = option.votes === Math.max(...activePoll.options.map(o => o.votes)) && option.votes > 0;
                  
                  return (
                    <div 
                      key={index} 
                      className={`relative p-6 rounded-2xl transition-all duration-500 ${
                        isLeading ? 'bg-blue-500/20 border-2 border-blue-500' : 'bg-white/5 border border-white/10'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-2xl font-medium">{option.text}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-4xl font-bold">{percentage}%</span>
                          {isLeading && <TrendingUp className="h-8 w-8 text-blue-400" />}
                        </div>
                      </div>
                      <Progress 
                        value={percentage} 
                        className="h-4 bg-white/10"
                        style={{ 
                          '--progress-background': isLeading ? '#3B82F6' : '#6B7280'
                        }}
                      />
                      <div className="absolute right-6 bottom-6 text-white/40 text-lg">
                        {option.votes} votes
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-8 text-center text-white/50 text-xl">
                <span className="font-semibold text-white">{totalVotes}</span> total votes
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-white/30">
              <BarChart3 className="h-24 w-24 mb-6" />
              <p className="text-2xl">No active poll</p>
              <p className="text-lg mt-2">Polls will appear here when launched</p>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="w-px bg-white/10" />

        {/* Right Panel - Top Questions */}
        <div className="w-full max-w-[500px] p-12 flex flex-col">
          <div className="flex items-center gap-4 mb-8">
            <MessageCircle className="h-10 w-10 text-emerald-400" />
            <h2 className="text-3xl font-bold">Top Questions</h2>
          </div>

          {topQuestions.length > 0 ? (
            <div className="space-y-4 flex-1 overflow-hidden">
              {topQuestions.map((question, index) => (
                <div 
                  key={question.id}
                  className={`p-6 rounded-2xl transition-all duration-300 ${
                    index === 0 
                      ? 'bg-emerald-500/20 border-2 border-emerald-500 scale-[1.02]' 
                      : 'bg-white/5 border border-white/10'
                  }`}
                  style={{ animation: `fadeSlideIn 0.5s ease-out ${index * 0.1}s both` }}
                >
                  <div className="flex items-start gap-4">
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl ${
                      index === 0 ? 'bg-emerald-500 text-white' : 'bg-white/10 text-white/60'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-lg ${index === 0 ? 'text-white font-medium' : 'text-white/80'}`}>
                        {question.text}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-white/50">
                        <span className="flex items-center gap-1">
                          <ThumbsUp className="h-4 w-4" />
                          {question.votes} votes
                        </span>
                        {question.author_name && (
                          <span>— {question.author_name}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-white/30">
              <Mic className="h-16 w-16 mb-4" />
              <p className="text-xl">No questions yet</p>
              <p className="text-base mt-2">Audience questions appear here</p>
            </div>
          )}

          {/* QR Code prompt */}
          <div className="mt-8 p-6 bg-white/5 rounded-2xl text-center">
            <p className="text-white/60 mb-2">Ask a question or vote</p>
            <p className="text-2xl font-bold text-blue-400">Scan the QR on your badge</p>
          </div>
        </div>
      </div>

      {/* Animation Keyframes */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(1.1); }
        }
        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
};

export default ProjectorView;
